
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

export interface EventFilterState {
  locationType: string;
  dateRange: string;
  priceType: string;
  selectedTags: string[];
}

interface EventFiltersProps {
  filters: EventFilterState;
  onChange: (filters: EventFilterState) => void;
  availableTags: string[];
}

const EventFilters: React.FC<EventFiltersProps> = ({ filters, onChange, availableTags }) => {
  const { t } = useTranslation('events');

  const update = (partial: Partial<EventFilterState>) => {
    onChange({ ...filters, ...partial });
  };

  const toggleTag = (tag: string) => {
    const next = filters.selectedTags.includes(tag)
      ? filters.selectedTags.filter(t => t !== tag)
      : [...filters.selectedTags, tag];
    update({ selectedTags: next });
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-3 items-center">
        {/* Location Type */}
        <Select value={filters.locationType} onValueChange={v => update({ locationType: v })}>
          <SelectTrigger className="w-[140px] h-9 text-sm">
            <SelectValue placeholder={t('filters.locationType')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('filters.all')}</SelectItem>
            <SelectItem value="virtual">{t('filters.virtual')}</SelectItem>
            <SelectItem value="in_person">{t('filters.inPerson')}</SelectItem>
            <SelectItem value="hybrid">{t('filters.hybrid')}</SelectItem>
          </SelectContent>
        </Select>

        {/* Date Range */}
        <Select value={filters.dateRange} onValueChange={v => update({ dateRange: v })}>
          <SelectTrigger className="w-[140px] h-9 text-sm">
            <SelectValue placeholder={t('filters.dateRange')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('filters.all')}</SelectItem>
            <SelectItem value="this_week">{t('filters.thisWeek')}</SelectItem>
            <SelectItem value="this_month">{t('filters.thisMonth')}</SelectItem>
            <SelectItem value="next_month">{t('filters.nextMonth')}</SelectItem>
          </SelectContent>
        </Select>

        {/* Price */}
        <Select value={filters.priceType} onValueChange={v => update({ priceType: v })}>
          <SelectTrigger className="w-[120px] h-9 text-sm">
            <SelectValue placeholder={t('filters.price')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('filters.all')}</SelectItem>
            <SelectItem value="free">{t('filters.freeOnly')}</SelectItem>
            <SelectItem value="paid">{t('filters.paidOnly')}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Tags */}
      {availableTags.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {availableTags.map(tag => (
            <Badge
              key={tag}
              variant={filters.selectedTags.includes(tag) ? 'default' : 'outline'}
              className="cursor-pointer text-xs"
              onClick={() => toggleTag(tag)}
            >
              {tag}
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
};

export default EventFilters;
